import React, { useState, useEffect } from 'react';
import {
  Table,
  Typography,
  Tooltip,
  Button,
  Row,
  Col,
  Popconfirm,
  message,
  Card,
  Modal,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ApiService from '../Components/apiService';
import Cookies from 'js-cookie';
import CreateNewButton from './CreateButton'; // Import the CreateGroup component
import { BASE_URL } from '../Components/Constant';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const CustomButtonList = (object) => {
  const [allCustomActions, setAllCustomActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [isCreatingOrEditing, setIsCreatingOrEditing] = useState(false); // To toggle between list and form view
  const [editingData,setIseditingData]  = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const security = useSelector((state) => state.data.items[0]);
  const navigate = useNavigate();
  const [type, setType] = useState('component'); // default to 'component'

  const fetchCustomButtons = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/get_action_button/${object.object._id}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall(navigate);  
      const mappedResponse = response.map((button) => ({
        _id: button._id,
        name: button.name,
        apiName: button.apiname,
        component: button.component_name,
        class: button.action_info?.class_name,
        method: button.action_info?.method_name,
        action_info: button?.action_info,
        type: button?.type,
      }));
  
      setAllCustomActions(mappedResponse); //
    } catch (error) {
      console.error('Error fetching buttons:', error);
    //   message.error('Failed to fetch buttons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  
  const handleCreate = (actionType) => {
    if (security?.system_settings?.action_button?.create === false) {
      message.error('You do not have permission to create custom buttons');
      return;
    }
    setIseditingData([]);
    setType(actionType);
    setIsCreatingOrEditing(true);
  };
  

  const handleEdit = (record) => {
    console.log('Editing record:', record);
    
    if (security?.system_settings?.action_button?.edit === false) {
      message.error('You do not have permission to edit custom buttons');
      return;
    }
    setIseditingData(record);
    setType(record.type || 'component'); // If type is stored in record
    setIsCreatingOrEditing(true);
  };
  

  const handleDelete = async (buttonId) => {
    if(security?.system_settings?.action_button?.delete === false){
      message.error('You do not have permission to delete custom buttons');
      return;
    }
    try {
      // Call the delete API for the specific button
      const apiService = new ApiService(
        `${BASE_URL}/delete_button/${buttonId}`, // Use the correct endpoint to delete the button
        { 'Content-Type': 'application/json' },
        'DELETE'
      );
      const response = await apiService.makeCall(navigate);
      if (response && response.message) {
        message.success('Button deleted successfully');
        // Refetch the list of buttons after deletion
        fetchCustomButtons(); 
      }
    } catch (error) {
      console.error('Error deleting button:', error);
      message.error('Failed to delete button');
    }
  };

  const handleCancel = () => {
    setIsCreatingOrEditing(false); // Show the table again
  };

  
  
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <a onClick={() => handleEdit(record)}>{text}</a>
      ),
    },
    {
      title: 'API Name',
      dataIndex: 'apiName',
      key: 'apiName',
      sorter: (a, b) => a.apiName.localeCompare(b.apiName),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: [
        { text: 'Component', value: 'component' },
        { text: 'URL', value: 'url' },
      ],
      onFilter: (value, record) => record.type === value,
      render: (type) => (
        <span style={{ textTransform: 'capitalize' }}>
          {type === 'url' ? 'URL' : 'Component'}
        </span>
      ),
    },
    {
      title: 'Component / URL',
      dataIndex: 'component',
      key: 'component',
      sorter: (a, b) => (a.component || '').localeCompare(b.component || ''),
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ wordBreak: 'break-word' }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              onClick={() => handleEdit(record)}
              style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined
                style={{ color: 'red', fontSize: '12px', cursor: 'pointer' }}
              />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];
  
  useEffect(() => {
    if (security?.system_settings?.action_button?.view === false) {
      message.error('You do not have permission to view custom buttons');
      return;
    }
    fetchCustomButtons();
  }, []);

  return (
    <div>
      {isCreatingOrEditing ? (
        <CreateNewButton
          setIsCreatingOrEditing={setIsCreatingOrEditing}
          isCreatingOrEditing={isCreatingOrEditing}
          onCancel={handleCancel}
          userList={users}
          editingData={editingData}
          object={object}
          fetchCustomButtons={fetchCustomButtons}
          type={type} 
        />
      ) : (
        <>
          <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
            <Col>
              <Title level={4} >
                Custom Action Buttons
              </Title>
            </Col>
            <Col>
              <Button type="primary" onClick={() => handleCreate('component')}>
                New Component Action
              </Button>
              <Button type="primary" onClick={() => handleCreate('url')} style={{ marginLeft: 8 }}>
                New URL Action
              </Button>
              <Button type="primary" onClick={() => handleCreate('custom')} style={{ marginLeft: 8 }}>
                Custom Action
              </Button>
            </Col>

          </Row>
          <Table
            columns={columns}
            dataSource={allCustomActions}
            rowKey="_id"
            loading={loading}
            scroll={{ y: 550 }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },              
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </>
      )}
    </div>
  );
};

export default CustomButtonList;
